<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div>
        <h4 class="mb-0">
          Распознавание номеров
        </h4>
        <div class="text-muted small">
          Очередь фото для сервиса bib-recognizer. Воркеры сами забирают задачи; найденные номера становятся bib-тегами
          фото (источник «auto»).
        </div>
      </div>
      <b-button
        variant="outline-secondary"
        :disabled="loading"
        @click="refresh"
      >
        Обновить
      </b-button>
    </div>

    <b-alert
      v-if="errorText"
      show
      variant="danger"
    >
      {{ errorText }}
    </b-alert>

    <b-card
      v-if="stats"
      no-body
      class="mb-1"
    >
      <b-card-body class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="mb-50">
          <div class="d-flex align-items-center mb-50">
            <b-badge
              :variant="stats.enabled ? 'success' : 'warning'"
              class="mr-1"
            >
              {{ stats.enabled ? 'Очередь работает' : 'Пауза' }}
            </b-badge>
            <span class="text-muted">
              В работе {{ stats.in_flight }} из {{ stats.max_in_flight }}
              · новые фото {{ stats.auto_enqueue ? 'ставятся в очередь автоматически' : 'в очередь не ставятся' }}
            </span>
          </div>
          <div class="small text-muted">
            Последняя выдача воркеру: <strong>{{ formatDate(stats.last_claimed_at) }}</strong>
            · последний результат: <strong>{{ formatDate(stats.last_finished_at) }}</strong>
          </div>
        </div>
        <div class="d-flex flex-wrap">
          <b-button
            :variant="stats.enabled ? 'outline-warning' : 'success'"
            class="mr-50 mb-50"
            :disabled="busy"
            @click="stats.enabled ? stopService() : startService()"
          >
            {{ stats.enabled ? 'Остановить' : 'Запустить' }}
          </b-button>
          <b-button
            variant="outline-primary"
            class="mb-50"
            @click="$refs.settingsModal.show()"
          >
            Настройки очереди
          </b-button>
        </div>
      </b-card-body>
      <b-card-body
        v-if="service"
        class="border-top py-1"
      >
        <div class="d-flex flex-wrap align-items-center">
          <b-badge
            :variant="serviceBadge.variant"
            class="mr-1"
          >
            {{ serviceBadge.text }}
          </b-badge>
          <span class="text-muted small">{{ serviceHint }}</span>
        </div>
        <div
          v-if="service.runs.length"
          class="small text-muted mt-50"
        >
          Последние вызовы:
          <span
            v-for="(run, index) in service.runs.slice(0, 5)"
            :key="index"
            class="mr-1"
            :class="{ 'text-danger': run.error }"
          >
            {{ formatTime(run.started_at) }}
            <template v-if="!run.finished_at">— идёт</template>
            <template v-else-if="run.error">— ошибка</template>
            <template v-else>— {{ run.done }} фото за {{ duration(run) }}</template>
          </span>
          <div
            v-if="service.runs[0].error"
            class="text-danger"
          >
            {{ service.runs[0].error }}
          </div>
        </div>
      </b-card-body>
      <b-card-footer class="d-flex flex-wrap">
        <div
          v-for="status in statuses"
          :key="status.value"
          class="status-count mr-2"
        >
          <b-badge :variant="status.variant">
            {{ status.text }}
          </b-badge>
          <strong class="ml-50">{{ stats.counts[status.value] }}</strong>
        </div>
      </b-card-footer>
    </b-card>

    <div class="d-flex flex-wrap align-items-center justify-content-between mb-50">
      <h5 class="mb-0">
        Старты
      </h5>
      <b-form
        class="d-flex align-items-center"
        @submit.prevent="enqueueNewStart"
      >
        <b-form-input
          v-model.number="newStartId"
          type="number"
          min="1"
          size="sm"
          placeholder="ID старта"
          class="start-input mr-50"
        />
        <b-button
          type="submit"
          variant="primary"
          size="sm"
          :disabled="busy || !newStartId"
        >
          Поставить в очередь
        </b-button>
      </b-form>
    </div>

    <b-card
      no-body
      class="mb-1"
    >
      <b-table
        :items="starts"
        :fields="startFields"
        small
        striped
        responsive
        show-empty
        empty-text="В очереди ещё не было фото"
        :busy="loading"
      >
        <template #cell(start)="data">
          <a
            href="#"
            @click.prevent="filterByStart(data.item.start_id)"
          >#{{ data.item.start_id }}</a>
          {{ data.item.start_name || '' }}
        </template>
        <template #cell(progress)="data">
          <div class="d-flex align-items-center">
            <b-progress
              :max="total(data.item.counts)"
              class="flex-grow-1 mr-50"
              height="8px"
            >
              <b-progress-bar
                :value="data.item.counts.done"
                variant="success"
              />
              <b-progress-bar
                :value="data.item.counts.processing"
                variant="info"
              />
              <b-progress-bar
                :value="data.item.counts.failed"
                variant="danger"
              />
            </b-progress>
            <span class="small text-nowrap">{{ data.item.counts.done }} / {{ total(data.item.counts) }}</span>
          </div>
        </template>
        <template #cell(counts)="data">
          <span class="small text-nowrap">
            ждут {{ data.item.counts.pending }} · в работе {{ data.item.counts.processing }}
            · ошибки {{ data.item.counts.failed }} · отменены {{ data.item.counts.cancelled }}
          </span>
        </template>
        <template #cell(actions)="data">
          <!-- Fixed positioning: the responsive table wrapper scrolls (overflow) and would clip the menu -->
          <b-dropdown
            text="Действия"
            size="sm"
            variant="outline-secondary"
            right
            boundary="window"
            :popper-opts="{ positionFixed: true }"
            :disabled="busy"
          >
            <b-dropdown-item @click="enqueue(data.item.start_id, false)">
              Добавить новые фото
            </b-dropdown-item>
            <b-dropdown-item @click="confirmReindex(data.item)">
              Распознать заново все фото
            </b-dropdown-item>
            <b-dropdown-item
              :disabled="!data.item.counts.done"
              @click="confirmRetag(data.item)"
            >
              Пересчитать теги по протоколу
            </b-dropdown-item>
            <b-dropdown-item @click="openPriority(data.item)">
              Приоритет…
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item
              :disabled="!data.item.counts.failed"
              @click="retryFailed(data.item.start_id)"
            >
              Повторить ошибки ({{ data.item.counts.failed }})
            </b-dropdown-item>
            <b-dropdown-item
              :disabled="!data.item.counts.pending"
              variant="danger"
              @click="cancelPending(data.item.start_id)"
            >
              Отменить ожидающие ({{ data.item.counts.pending }})
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <div class="d-flex flex-wrap align-items-center justify-content-between mb-50">
      <h5 class="mb-0">
        Задачи
      </h5>
      <div class="d-flex align-items-center">
        <b-form-input
          v-model.number="jobFilter.start_id"
          type="number"
          min="1"
          size="sm"
          placeholder="ID старта"
          class="start-input mr-50"
          @change="loadJobs(1)"
        />
        <b-form-select
          v-model="jobFilter.status"
          :options="[{ value: null, text: 'Все статусы' }, ...statuses]"
          size="sm"
          class="mr-50"
          @change="loadJobs(1)"
        />
        <b-button
          v-if="jobFilter.start_id || jobFilter.status"
          variant="flat-secondary"
          size="sm"
          @click="clearFilter"
        >
          Сбросить
        </b-button>
      </div>
    </div>

    <b-card no-body>
      <b-table
        :items="jobs"
        :fields="jobFields"
        small
        responsive
        show-empty
        empty-text="Задач нет"
        :busy="jobsLoading"
      >
        <template #cell(photo)="data">
          <a
            v-if="data.item.image_url"
            :href="data.item.image_url"
            target="_blank"
            rel="noopener"
          >
            <img
              :src="thumbnail(data.item.image_url)"
              class="job-thumb"
              alt=""
              loading="lazy"
            >
          </a>
          <div class="small text-muted">
            фото #{{ data.item.photo_id }} · старт #{{ data.item.start_id }}
          </div>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusText(data.item.status) }}
          </b-badge>
          <div class="small text-muted">
            попыток {{ data.item.attempts }}<template v-if="data.item.priority">
              · приоритет {{ data.item.priority }}
            </template>
          </div>
        </template>
        <template #cell(result)="data">
          <div v-if="data.item.tagged && data.item.tagged.length">
            Теги:
            <b-badge
              v-for="number in data.item.tagged"
              :key="number"
              variant="light-success"
              class="mr-25"
            >
              {{ number }}
            </b-badge>
          </div>
          <div
            v-if="untagged(data.item).length"
            class="small text-muted"
          >
            Не стали тегами:
            <span
              v-for="bib in untagged(data.item)"
              :key="bib.number + bib.match"
              class="mr-50"
            >{{ bib.number }} ({{ bib.match }}, {{ bib.score.toFixed(2) }})</span>
          </div>
          <div
            v-if="data.item.last_error"
            class="small text-danger"
          >
            {{ data.item.last_error }}
          </div>
        </template>
        <template #cell(updated_at)="data">
          <span class="small">{{ formatDate(data.item.updated_at) }}</span>
          <div
            v-if="data.item.duration_ms"
            class="small text-muted"
          >
            {{ (data.item.duration_ms / 1000).toFixed(1) }} с · {{ data.item.worker_id }}
          </div>
        </template>
        <template #cell(actions)="data">
          <b-button
            variant="flat-primary"
            size="sm"
            :disabled="busy || data.item.status === 'processing'"
            @click="requeuePhoto(data.item)"
          >
            В очередь
          </b-button>
        </template>
      </b-table>
      <b-card-footer
        v-if="jobsCount > pageSize"
        class="d-flex justify-content-end"
      >
        <b-pagination
          :value="jobsPage"
          :total-rows="jobsCount"
          :per-page="pageSize"
          size="sm"
          class="mb-0"
          @input="loadJobs"
        />
      </b-card-footer>
    </b-card>

    <b-modal
      ref="settingsModal"
      title="Настройки очереди"
      size="lg"
      ok-only
      ok-title="Закрыть"
      @hidden="refresh"
    >
      <settings-category-form category="bib_recognition" />
    </b-modal>

    <b-modal
      ref="priorityModal"
      title="Приоритет старта"
      ok-title="Сохранить"
      cancel-title="Отмена"
      :ok-disabled="busy || !Number.isInteger(priority.value)"
      @ok="savePriority"
    >
      <p v-if="priority.start">
        Ожидающие фото старта #{{ priority.start.start_id }} {{ priority.start.start_name }} получат этот приоритет.
        Больше — раньше; по умолчанию 0.
      </p>
      <b-form-input
        v-model.number="priority.value"
        type="number"
        min="-1000"
        max="1000"
        step="1"
      />
    </b-modal>

    <b-modal
      ref="retagModal"
      title="Пересчитать теги?"
      ok-title="Пересчитать"
      cancel-title="Отмена"
      :ok-disabled="busy"
      @ok="retag"
    >
      <p v-if="retagStart">
        Сохранённые результаты {{ retagStart.counts.done }} фото старта #{{ retagStart.start_id }}
        {{ retagStart.start_name }} будут сверены с текущим протоколом и настройками тегов. Фото заново не
        распознаются, контейнер не вызывается.
      </p>
      <p class="small text-muted mb-0">
        Автоматические теги заменятся, ручные останутся. Числа, которые распознаватель отбросил, потому что их не было
        в протоколе на момент распознавания, вернуть так нельзя — для этого «Распознать заново».
      </p>
    </b-modal>

    <b-modal
      ref="reindexModal"
      title="Распознать заново?"
      ok-title="Распознать заново"
      cancel-title="Отмена"
      :ok-disabled="busy"
      @ok="reindex"
    >
      <p v-if="reindexStart">
        Все фото старта #{{ reindexStart.start_id }} {{ reindexStart.start_name }} вернутся в очередь. Автоматические
        теги заменятся новыми результатами, теги, поставленные вручную, останутся.
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BCardBody, BCardFooter, BDropdown, BDropdownDivider, BDropdownItem, BForm,
  BFormInput, BFormSelect, BModal, BPagination, BProgress, BProgressBar, BTable,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import bibRecognitionApi from '@/services/bib-recognition-api.service'
import SettingsCategoryForm from '@/views/settings/SettingsCategoryForm.vue'

const STATUSES = [
  { value: 'pending', text: 'Ждут', variant: 'secondary' },
  { value: 'processing', text: 'В работе', variant: 'info' },
  { value: 'done', text: 'Готово', variant: 'success' },
  { value: 'failed', text: 'Ошибка', variant: 'danger' },
  { value: 'cancelled', text: 'Отменены', variant: 'light-secondary' },
]

const IMGPROXY = 'https://img.sportsauce.ru'

export default {
  name: 'BibRecognitionPage',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardFooter,
    BDropdown,
    BDropdownDivider,
    BDropdownItem,
    BForm,
    BFormInput,
    BFormSelect,
    BModal,
    BPagination,
    BProgress,
    BProgressBar,
    BTable,
    SettingsCategoryForm,
  },
  data() {
    return {
      statuses: STATUSES,
      stats: null,
      service: null,
      timer: null,
      starts: [],
      jobs: [],
      jobsCount: 0,
      jobsPage: 1,
      pageSize: 25,
      jobFilter: { start_id: null, status: null },
      loading: false,
      jobsLoading: false,
      busy: false,
      errorText: '',
      newStartId: null,
      priority: { start: null, value: 0 },
      reindexStart: null,
      retagStart: null,
      startFields: [
        { key: 'start', label: 'Старт' },
        { key: 'progress', label: 'Готово', thStyle: { width: '220px' } },
        { key: 'counts', label: 'Статусы' },
        { key: 'actions', label: '' },
      ],
      jobFields: [
        { key: 'photo', label: 'Фото', thStyle: { width: '150px' } },
        { key: 'status', label: 'Статус' },
        { key: 'result', label: 'Результат' },
        { key: 'updated_at', label: 'Обновлено' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    serviceBadge() {
      if (!this.service.configured) return { variant: 'light-secondary', text: 'Контейнер не подключён' }
      if (this.service.active) return { variant: 'info', text: `Работает: ${this.service.active} из ${this.service.max_instances}` }
      return { variant: 'light-success', text: 'Погашен' }
    },
    serviceHint() {
      if (!this.service.configured) {
        return 'Нет BIB_SERVERLESS_URL / BIB_SERVERLESS_API_KEY: фото распознают только внешние воркеры.'
      }
      if (this.service.active) return 'Serverless-контейнер распознаёт фото и погаснет сам, когда очередь опустеет.'
      if (!this.service.enabled) return 'Очередь остановлена — контейнер не вызывается и ничего не стоит.'
      if (this.service.pending) return 'Фото ждут — контейнер вызовется в течение 15 секунд (первый запуск дольше).'
      return 'Очередь пуста — контейнер не запущен и ничего не стоит. Новые фото запустят его сами.'
    },
  },
  async created() {
    await this.refresh()
    // While photos are being recognized the counters move: follow them without a manual refresh
    this.timer = setInterval(() => {
      const working = this.service?.active || this.stats?.counts.processing
      if (working && !this.busy && !document.hidden) this.refresh()
    }, 15000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    async refresh() {
      this.loading = true
      this.errorText = ''
      try {
        const [stats, starts, service] = await Promise.all([
          bibRecognitionApi.getStats(),
          bibRecognitionApi.getStarts(),
          bibRecognitionApi.getService(),
        ])
        this.stats = stats.data
        this.starts = starts.data
        this.service = service.data
      } catch (e) {
        this.errorText = this.errorMessage(e)
      } finally {
        this.loading = false
      }
      await this.loadJobs(this.jobsPage)
    },
    async loadJobs(page = 1) {
      this.jobsLoading = true
      this.jobsPage = page
      try {
        const res = await bibRecognitionApi.getJobs({
          start_id: this.jobFilter.start_id || undefined,
          status: this.jobFilter.status || undefined,
          skipCount: (page - 1) * this.pageSize,
          maxResultCount: this.pageSize,
        })
        this.jobs = res.data.rows
        this.jobsCount = res.data.count
      } catch (e) {
        this.errorText = this.errorMessage(e)
      } finally {
        this.jobsLoading = false
      }
    },
    /** Runs one change, then reloads everything so the page shows what the backend has */
    async mutate(action, successText) {
      if (this.busy) return false
      this.busy = true
      try {
        const res = await action()
        this.toast('Готово', typeof successText === 'function' ? successText(res.data) : successText, 'success')
        return true
      } catch (e) {
        this.toast('Ошибка', this.errorMessage(e), 'danger')
        return false
      } finally {
        this.busy = false
        await this.refresh()
      }
    },
    startService() {
      this.mutate(
        () => bibRecognitionApi.startService(),
        data => (data.opened ? `Очередь запущена, вызовов контейнера: ${data.opened}` : 'Очередь запущена'),
      )
    },
    stopService() {
      this.mutate(
        () => bibRecognitionApi.stopService(),
        'Остановлено: контейнер доработает взятые фото и погаснет',
      )
    },
    duration(run) {
      const seconds = Math.round((new Date(run.finished_at) - new Date(run.started_at)) / 1000)
      return seconds < 60 ? `${seconds} с` : `${Math.floor(seconds / 60)} мин ${seconds % 60} с`
    },
    formatTime(value) {
      return new Date(value).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    },
    enqueue(startId, reset) {
      return this.mutate(
        () => bibRecognitionApi.enqueueStart(startId, { reset }),
        data => `Старт #${startId}: добавлено ${data.added}${reset ? `, возвращено в очередь ${data.reset}` : ''}`,
      )
    },
    async enqueueNewStart() {
      if (await this.enqueue(this.newStartId, false)) this.newStartId = null
    },
    confirmReindex(start) {
      this.reindexStart = start
      this.$refs.reindexModal.show()
    },
    reindex() {
      this.enqueue(this.reindexStart.start_id, true)
    },
    confirmRetag(start) {
      this.retagStart = start
      this.$refs.retagModal.show()
    },
    retag() {
      const { start_id: startId } = this.retagStart
      this.mutate(
        () => bibRecognitionApi.retagStart(startId),
        data => `Старт #${startId}: ${data.tags} тегов на ${data.tagged_photos} из ${data.photos} фото`,
      )
    },
    openPriority(start) {
      this.priority = { start, value: 0 }
      this.$refs.priorityModal.show()
    },
    savePriority() {
      const { start, value } = this.priority
      this.mutate(
        () => bibRecognitionApi.setStartPriority(start.start_id, value),
        data => `Приоритет ${value} у ${data.updated} ожидающих фото`,
      )
    },
    retryFailed(startId) {
      this.mutate(() => bibRecognitionApi.retryFailed(startId), data => `Возвращено в очередь: ${data.requeued}`)
    },
    cancelPending(startId) {
      this.mutate(() => bibRecognitionApi.cancelPending(startId), data => `Отменено: ${data.cancelled}`)
    },
    requeuePhoto(job) {
      this.mutate(() => bibRecognitionApi.enqueuePhoto(job.photo_id), `Фото #${job.photo_id} снова в очереди`)
    },
    filterByStart(startId) {
      this.jobFilter.start_id = startId
      this.loadJobs(1)
    },
    clearFilter() {
      this.jobFilter = { start_id: null, status: null }
      this.loadJobs(1)
    },
    total(counts) {
      return Object.values(counts).reduce((sum, count) => sum + count, 0)
    },
    untagged(job) {
      const tagged = new Set(job.tagged || [])
      return (job.bibs || []).filter(bib => !tagged.has(bib.number.toLowerCase()))
    },
    statusText(value) {
      return (STATUSES.find(status => status.value === value) || {}).text || value
    },
    statusVariant(value) {
      return (STATUSES.find(status => status.value === value) || {}).variant || 'secondary'
    },
    thumbnail(url) {
      return `${IMGPROXY}/insecure/rs:fit:240:240/plain/${encodeURI(url)}`
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString('ru-RU') : '—'
    },
    toast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: variant === 'success' ? 'BellIcon' : 'AlertTriangleIcon',
        },
      })
    },
    errorMessage(error) {
      const message = error?.response?.data?.message
      if (Array.isArray(message)) return message.join('; ')
      return message || 'Не удалось выполнить запрос'
    },
  },
}
</script>

<style scoped>
.start-input {
  width: 120px;
}
.job-thumb {
  max-width: 120px;
  max-height: 80px;
  border-radius: 0.357rem;
}
.status-count {
  white-space: nowrap;
}
</style>
